import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Reveal, FadeIn } from "../components/Motion";
import CourseCard from "../components/CourseCard";

const FILTERS = [
  { key: "all", label: "All Programs" },
  { key: "teacher-training", label: "Teacher Training" },
  { key: "language", label: "Language & IELTS" },
  { key: "vocational", label: "Vocational" },
];

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/courses")
      .then((r) => setCourses(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (filter === "all" ? courses : courses.filter((c) => c.category === filter)),
    [courses, filter],
  );

  return (
    <div data-testid="courses-page" className="pt-32 lg:pt-40">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Courses & Training</p>
        </FadeIn>
        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold leading-[1.08] tracking-tight text-forest-deep sm:text-5xl lg:text-6xl">
          <Reveal y={24}>Choose the course</Reveal>
          <Reveal y={24} delay={0.1}>
            <span className="italic text-terracotta">that chooses your future.</span>
          </Reveal>
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Seven certified programs across teacher training, language mastery and vocational skills — every one with
            100% placement assistance.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <Link
            to="/quiz"
            data-testid="quiz-banner"
            className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-forest px-8 py-6 text-cream transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-forest/20"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Free · 2 Minutes · 15 Questions</p>
              <p className="mt-1 font-serif text-2xl font-semibold">Not sure of your English level? Take the test.</p>
            </div>
            <span className="rounded-full bg-gold px-6 py-3 font-display text-sm font-semibold text-forest-deep">Start the Test →</span>
          </Link>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="course-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-5 py-2.5 font-display text-sm font-medium transition-all duration-300 ${
                  filter === f.key
                    ? "bg-forest text-cream shadow-lg shadow-forest/20"
                    : "border border-forest/20 bg-white text-forest hover:border-forest/50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">
          {loading &&
            [0, 1, 2].map((i) => (
              <div key={i} className="h-96 animate-pulse rounded-3xl bg-linen" data-testid={`course-skeleton-${i}`} />
            ))}
          {!loading && visible.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <CourseCard course={c} />
            </Reveal>
          ))}
          {!loading && visible.length === 0 && (
            <p className="col-span-full py-16 text-center text-stone-500" data-testid="courses-empty">
              No courses in this category yet.
            </p>
          )}
        </div>
      </section>

      {/* Eligibility matrix */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="matrix-section">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">At a Glance</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-forest-deep sm:text-4xl">Course comparison matrix.</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 overflow-x-auto rounded-3xl border border-forest/10 bg-white" data-testid="course-matrix-table">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-forest/10 font-mono text-[11px] uppercase tracking-[0.18em] text-stone-500">
                  <th className="px-6 py-4">Program</th>
                  <th className="px-6 py-4">Duration</th>
                  <th className="px-6 py-4">Eligibility</th>
                  <th className="px-6 py-4">Fee</th>
                  <th className="px-6 py-4">Placement</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((c) => (
                  <tr key={c.id} className="border-b border-forest/5 last:border-0 hover:bg-linen/50" data-testid={`matrix-row-${c.slug}`}>
                    <td className="px-6 py-4 font-display font-semibold text-forest-deep">{c.title}</td>
                    <td className="px-6 py-4 text-stone-600">{c.duration}</td>
                    <td className="px-6 py-4 text-stone-600">{c.eligibility}</td>
                    <td className="px-6 py-4 text-stone-600">{c.fee}</td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-forest/10 px-3 py-1 text-xs font-medium text-forest">Assisted</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
